import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { validateBreadcrumbPath } from '../utils/breadcrumbUtils';

/**
 * Additional validation for breadcrumb path to prevent duplicates
 */
const cleanBreadcrumbPath = (path) => {
  const validatedPath = validateBreadcrumbPath(path);
  
  // Remove consecutive duplicates based on label and route
  const deduplicatedPath = [];
  let lastItem = null;
  
  for (const item of validatedPath) {
    if (!lastItem || 
        lastItem.label !== item.label || 
        lastItem.route !== item.route) {
      deduplicatedPath.push(item);
      lastItem = item;
    }
  }
  
  return deduplicatedPath;
};

const Breadcrumb = ({ customPath = null, mobileState }) => {
  const location = useLocation();
  
  const getPageTitle = (pathname) => {
    const routes = {
      '/': 'Ciao',
      '/southington': '06489',
      '/futbol': 'Futbol',
      '/family-tree': 'Rossetti',
      '/family-stats': 'Stats',
      '/calendar': 'Calendar',
      '/wedding': 'Wedding',
      '/thanksgiving': 'Thanksgiving',
      '/pane': 'Pane (Bread)',
      '/rossetti': 'Rossetti Heritage',
      '/plymouth': 'Plymouth',
      '/rawrock': 'Raw Rock',
      '/tomatoes': 'Tomatoes',
      '/april-twelve': 'April Twelve'
    };
    
    // Handle dynamic routes
    if (pathname.startsWith('/person/')) {
      return 'Person Details';
    }
    if (pathname.startsWith('/gallery/')) {
      const galleryType = pathname.split('/')[2];
      const galleryNames = {
        'rossetti': 'Rossetti Gallery',
        'wedding': 'Wedding Gallery',
        'futbol': 'Futbol Gallery',
        'pane': 'Pane Gallery',
        'plymouth': 'Plymouth Gallery',
        'rawrock': 'Raw Rock Gallery',
        'tomatoes': 'Tomatoes Gallery'
      };
      return galleryNames[galleryType] || 'Gallery';
    }
    
    return routes[pathname] || 'Page';
  };

  const getParentPath = (pathname) => {
    const parentRoutes = {
      // Only pages that are truly sub-pages should have parents
      '/thanksgiving': { path: '/', title: 'Ciao' },  // Thanksgiving is related to home
      // All other pages in navigation are top-level
      '/wedding': null,        // Wedding is top-level in navigation
      '/family-stats': null,   // Stats is top-level in navigation  
      '/calendar': null,       // Calendar is top-level in navigation
      '/plymouth': null,
      '/rawrock': null,
      '/tomatoes': null,
      '/april-twelve': null
    };
    
    // Handle dynamic routes - these have clear hierarchical relationships
    if (pathname.startsWith('/person/')) {
      return { path: '/family-tree', title: 'Rossetti' };
    }
    if (pathname.startsWith('/gallery/')) {
      // Galleries could be related to their main pages
      const galleryType = pathname.split('/')[2];
      const galleryParents = {
        'rossetti': { path: '/family-tree', title: 'Rossetti' },
        'wedding': { path: '/wedding', title: 'Wedding' },
        'futbol': { path: '/futbol', title: 'Futbol' },
        'pane': { path: '/', title: 'Ciao' }
      };
      return galleryParents[galleryType] || null;
    }
    
    return parentRoutes[pathname] || null;
  };

  // Don't show breadcrumb on home page
  if (location.pathname === '/') {
    return null;
  }

  // Use custom path if provided, otherwise use default logic
  if (customPath && customPath.length > 0) {
    // Validate and clean the custom path to prevent duplicates
    const cleanedPath = cleanBreadcrumbPath(customPath);
    
    return (
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <div className="breadcrumb-container">
          <Link to="/" className="breadcrumb-home">
            <i className="fas fa-home"></i>
            Ciao
          </Link>
          {cleanedPath.map((item, index) => (
            <React.Fragment key={index}>
              <span className="breadcrumb-separator">
                <i className="fas fa-chevron-right"></i>
              </span>
              {item.route ? (
                <Link to={item.route} className="breadcrumb-link">
                  {item.icon && <i className={item.icon}></i>}
                  {item.label}
                </Link>
              ) : (
                <span className="breadcrumb-current">
                  {item.icon && <i className={item.icon}></i>}
                  {item.label}
                </span>
              )}
            </React.Fragment>
          ))}
        </div>
      </nav>
    );
  }

  const parentPath = getParentPath(location.pathname);

  return (
    <nav className="breadcrumb" aria-label="Breadcrumb">
      <div className="breadcrumb-container">
        <Link to="/" className="breadcrumb-home">
          <i className="fas fa-home"></i>
          Ciao
        </Link>
        <span className="breadcrumb-separator">
          <i className="fas fa-chevron-right"></i>
        </span>
        {parentPath && (
          <>
            <Link to={parentPath.path} className="breadcrumb-link">
              {parentPath.title}
            </Link>
            <span className="breadcrumb-separator">
              <i className="fas fa-chevron-right"></i>
            </span>
          </>
        )}
        <span className="breadcrumb-current">
          {getPageTitle(location.pathname)}
        </span>
      </div>
    </nav>
  );
};

export default Breadcrumb;